import { useZero } from "@rocicorp/zero/react";
import { type ReactNode, useState } from "react";
import {
  certificateMembership,
  trimmedCaCertificate,
  withoutTrustedCertificate,
  withTrustedCertificate,
} from "../../../shared/connectors/caCertificate";
import { certificateProbeTarget } from "../../../shared/connectors/certificateProbeTarget";
import {
  readPresentedCertificate,
  STUCK_PENDING_MS,
} from "../../../shared/connectors/presentedCertificate";
import type { ConnectorDefinition } from "../../../shared/connectors/types";
import { CONNECTOR_CERTIFICATE_TRUST_FLAG } from "../../../shared/featureFlagNames";
import { generateId } from "../../../shared/id";
import { sharedMutators } from "../../../shared/mutators";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useNow } from "../../hooks/useNow";
import {
  queueConnectorEdit,
  withConnectorDestinationProof,
} from "../../sealSecret";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { styled } from "goober";
import { Box } from "../Box";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { HelpTooltip } from "../HelpTooltip";
import { Icon, type IconName } from "../Icon";
import { IntegrationButton } from "../IntegrationButton";
import {
  DetailLabel,
  DetailList,
  DetailRow,
  NeutralIconBadge,
  RowNameLine,
} from "../Settings/shared";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import {
  Actions,
  AuthorizedButton,
  AuthorizedInfo,
  AuthorizedLabel,
  CheckIcon,
  IconButton,
} from "./authorizedChip";
import { CertificateDetails } from "./CertificateDetails";
import { coversHost, useCertificateDescriptions } from "./describeCertificate";
import type { App } from "./Form";

const STOP_REASONS: Record<string, string> = {
  no_issuer_uri: "The last certificate doesn’t say where to fetch its issuer.",
  denied:
    "3B couldn’t fetch the next certificate because its host isn’t allowed by this tenant’s egress rules or is a private address.",
  fetch_failed: "3B couldn’t fetch the next certificate from its issuer URL.",
  too_large: "The issuer’s response was too large.",
  unsupported_format: "The issuer’s response wasn’t in a supported format.",
  not_issuer:
    "The certificate at the issuer URL didn’t issue the one before it.",
  hop_limit: "The chain is longer than 3B follows.",
  timeout: "Fetching the rest of the chain took too long.",
};

const Rows = styled(DetailList)`
  &:empty {
    display: none;
  }
`;

type SectionProps = {
  app: App;
  definition: ConnectorDefinition;
  field: string;
  disabled?: boolean;
  onEdit?: () => void;
  saveError: boolean;
  onSaveStatus: (field: string, saved: boolean) => void;
};

type ProbeTarget = Exclude<
  ReturnType<typeof certificateProbeTarget>,
  { kind: "none" }
>;

export function TrustedCertificateField(props: SectionProps) {
  const enabled = useFeatureFlag(CONNECTOR_CERTIFICATE_TRUST_FLAG);
  if (!enabled) return null;
  const target = certificateProbeTarget(props.definition, {
    urlPattern: props.app.urlPattern,
    configuration: props.app.configuration,
  });
  if (target.kind === "none") return null;
  return (
    <ServerCertificate
      app={props.app}
      field={props.field}
      disabled={props.disabled}
      onEdit={props.onEdit}
      saveError={props.saveError}
      onSaveStatus={props.onSaveStatus}
      target={target}
    />
  );
}

function ServerCertificate({
  app,
  field,
  target,
  disabled,
  onEdit,
  saveError,
  onSaveStatus,
}: Omit<SectionProps, "definition"> & { target: ProbeTarget }) {
  const z = useZero();
  const [confirmRemoveAllOpen, setConfirmRemoveAllOpen] = useState(false);
  const presented = readPresentedCertificate(app.metadata);
  const trustedPem = trimmedCaCertificate(app.configuration[field]);
  const trusted = useCertificateDescriptions(trustedPem);
  const succeeded = presented.status === "success";
  const issuers = presented.chain?.issuers ?? [];
  const fetched = useCertificateDescriptions(
    succeeded ? presented.certificate : undefined
  );
  const issuerDescriptions =
    useCertificateDescriptions(
      succeeded
        ? issuers.map((issuer) => issuer.certificate).join("\n")
        : undefined
    ) ?? [];
  const pending = presented.status === "pending";
  const now = useNow(pending ? 1000 : 60_000);
  const stuck =
    pending && now - Number(presented.requestedAt) >= STUCK_PENDING_MS;
  const [leaf] = fetched ?? [];
  const unreadable = succeeded && fetched !== undefined && !leaf;
  const chain = succeeded && leaf ? [leaf, ...issuerDescriptions] : [];
  const trustedOnly = (trusted ?? []).filter(
    (description) => !chain.some((card) => card.sha256 === description.sha256)
  );
  const host =
    target.kind === "missing"
      ? undefined
      : target.authority.replace(/:\d+$/, "");

  const inTrustSet = certificateMembership(trustedPem);
  const trustedCards = new Set(
    chain
      .filter((description) => inTrustSet(description.pem))
      .map((description) => description.sha256)
  );
  const aiaGap = (k: number) =>
    issuerDescriptions
      .slice(0, k)
      .filter(
        (description, i) =>
          issuers[i]?.source === "aia" && !trustedCards.has(description.sha256)
      );
  const covers = (description: { sha256: string }, j: number) =>
    trustedCards.has(description.sha256) && aiaGap(j).length === 0;

  const fetchCertificate = () =>
    z.mutate(
      sharedMutators.fetchConnectorCertificate({
        connectedAppId: app.id,
        fetchId: generateId(),
      })
    );
  const setTrustSet = (value: string | null) => {
    onEdit?.();
    onSaveStatus(field, true);
    void queueConnectorEdit(app.tenantId, app.id, () => {
      const update = withConnectorDestinationProof(app, {
        configurationPatch: { [field]: value },
      });
      const send = (
        args: Parameters<typeof sharedMutators.updateConnectedApp>[0]
      ) => z.mutate(sharedMutators.updateConnectedApp(args)).server;
      const result =
        update instanceof Promise ? update.then(send) : send(update);
      return result
        .then((response) => response.type === "success")
        .catch(() => false)
        .then((saved) => {
          onSaveStatus(field, saved);
        });
    }).catch(() => {
      onSaveStatus(field, false);
    });
  };
  const trust = (pems: string[]) =>
    setTrustSet(
      pems.reduce<string | undefined>(withTrustedCertificate, trustedPem) ??
        null
    );
  const remove = (pem: string) =>
    setTrustSet(withoutTrustedCertificate(trustedPem, pem));

  const fetching = pending && !stuck;
  let fetchLabel = "Fetch certificate";
  if (fetching) fetchLabel = "Fetching…";
  else if (stuck || presented.status === "failure" || unreadable)
    fetchLabel = "Try again";
  else if (trustedPem || succeeded) fetchLabel = "Check current certificate";

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <RowNameLine>
          <Text variant="text-small-md" color="var(--text-primary)">
            Server certificate
          </Text>
          {target.kind !== "missing" && (
            <HelpTooltip
              content={`3B connects to ${target.authority} through this connector’s network and shows the certificate the server presents.`}
            />
          )}
        </RowNameLine>
      </Box>
      <Box gap={8}>
        {saveError && (
          <div role="alert">
            <Text variant="text-small" color="var(--text-error)">
              Couldn’t save the trusted certificate. Re-enter the connector’s
              secrets and try again.
            </Text>
            <Button
              variant="subtle"
              onClick={() => {
                onSaveStatus(field, true);
              }}
            >
              Discard change
            </Button>
          </div>
        )}
        {target.kind !== "missing" &&
          (chain.length > 0 ? (
            <div>
              <AuthorizedButton>
                <AuthorizedLabel>
                  <Icon name="earth" size={16} />
                  <Text variant="text-regular-md">
                    Fetched from {presented.origin}
                  </Text>
                </AuthorizedLabel>
                <Actions>
                  <Tooltip content="Check current certificate">
                    <IconButton
                      type="button"
                      aria-label="Check current certificate"
                      onClick={fetchCertificate}
                      disabled={disabled}
                    >
                      <Icon name="refresh-cw" size={16} />
                    </IconButton>
                  </Tooltip>
                  {trustedPem && (
                    <Tooltip content="Remove all trusted certificates">
                      <IconButton
                        type="button"
                        aria-label="Remove all trusted certificates"
                        onClick={() => setConfirmRemoveAllOpen(true)}
                        disabled={disabled}
                      >
                        <Icon name="circle-minus" size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Actions>
              </AuthorizedButton>
              <AuthorizedInfo>
                <CheckIcon />
                <Text variant="text-small" color="var(--text-secondary)">
                  Fetched {formatRelativeTime(Number(presented.fetchedAt), now)}
                </Text>
              </AuthorizedInfo>
              <ConfirmModal
                open={confirmRemoveAllOpen}
                onClose={() => setConfirmRemoveAllOpen(false)}
                onConfirm={() => {
                  setTrustSet(null);
                  setConfirmRemoveAllOpen(false);
                }}
                title="Remove all trusted certificates?"
                description="3B stops trusting every certificate this connector trusts. Connections to servers that rely on them fail until you trust a certificate again."
                confirmLabel="Remove all"
                destructive
              />
            </div>
          ) : (
            <IntegrationButton
              type="button"
              onClick={fetchCertificate}
              disabled={disabled}
              isLoading={fetching}
              icon="refresh-cw"
              text={fetchLabel}
              style={{ alignSelf: "flex-start" }}
            />
          ))}
        <Rows>
          {target.kind === "missing" && (
            <StateRow
              icon="circle-slash"
              title="No server to fetch from"
              description={target.message}
            />
          )}
          {fetching && (
            <StateRow icon="clock" title="Fetching the server’s certificate…" />
          )}
          {stuck && (
            <StateRow
              alert
              icon="heart-crack"
              title="The fetch didn’t finish."
            />
          )}
          {presented.status === "failure" && (
            <StateRow
              alert
              icon="heart-crack"
              title="Couldn’t fetch the certificate"
              description={presented.message}
            />
          )}
          {unreadable && (
            <StateRow
              alert
              icon="heart-crack"
              title="The fetched certificate couldn’t be read."
            />
          )}
          {target.kind !== "missing" && !presented.status && !trustedPem && (
            <StateRow
              icon="shield-half"
              title="No certificate fetched yet"
              description="Fetch the server’s certificate to review it and choose what to trust."
            />
          )}
          {chain.length > 0 && presented.trusted === "true" && (
            <StateRow
              icon="shield-check"
              title="The server’s certificate is trusted."
            />
          )}
          {trustedOnly.map((description) => (
            <CertificateDetails
              key={description.sha256}
              description={description}
              now={now}
              tone="trusted"
              detailActions={
                <RemoveButton
                  disabled={disabled}
                  onRemove={() => remove(description.pem)}
                />
              }
              guidance={
                chain.length > 0 &&
                (!description.isCa ||
                  coversHost(
                    description.subjectAlternativeNames,
                    presented.origin!
                  )) &&
                "The server no longer presents this certificate."
              }
            />
          ))}
          {chain.map((description, i) => {
            const k = i - 1;
            const inBundle = trustedCards.has(description.sha256);
            const serverTrusted = presented.trusted === "true";
            const offerTrust = !inBundle && !serverTrusted;
            let actions: ReactNode = null;
            let detailActions: ReactNode = null;
            let guidance: ReactNode = null;
            if (inBundle) {
              detailActions = (
                <RemoveButton
                  disabled={disabled}
                  onRemove={() => remove(description.pem)}
                />
              );
            } else if (offerTrust && i === 0) {
              actions = (
                <Tooltip
                  content={
                    issuerDescriptions.some(covers)
                      ? "A trusted CA already covers this certificate. Trusting it also pins it exactly."
                      : "Trusting this certificate pins it exactly."
                  }
                >
                  <Button
                    variant="subtle"
                    disabled={disabled}
                    onClick={() => trust([description.pem])}
                  >
                    Trust this certificate
                  </Button>
                </Tooltip>
              );
            } else if (offerTrust && presented.chain?.issuerVerifiesLeaf) {
              const gap = aiaGap(k);
              actions = (
                <Tooltip
                  content={[
                    "Trusting this CA accepts certificates it issues for this connector’s hosts while they’re valid.",
                    gap.length > 0 &&
                      `It also trusts the intermediate ${gap.length === 1 ? "CA" : "CAs"} ${new Intl.ListFormat("en-US", { type: "conjunction" }).format(gap.map((card) => card.commonName))}, which the server doesn’t send.`,
                    issuerDescriptions.slice(0, k).some(covers) &&
                      "A trusted CA below it already covers this server, so this only adds a redundant anchor.",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <Button
                    variant="subtle"
                    disabled={disabled}
                    onClick={() =>
                      trust([...gap, description].map((card) => card.pem))
                    }
                  >
                    {gap.length === 0
                      ? "Trust this CA"
                      : `Trust this CA and ${gap.length} ${gap.length === 1 ? "intermediate" : "intermediates"}`}
                  </Button>
                </Tooltip>
              );
            } else if (offerTrust && k === 0) {
              guidance =
                "The issuing CA can’t be trusted for this server, because the server’s certificate doesn’t validate against it.";
            }
            let tone: "trusted" | "attention" | "neutral" = "neutral";
            if (inBundle || (i === 0 && serverTrusted)) tone = "trusted";
            else if (i === 0) tone = "attention";
            return (
              <CertificateDetails
                key={description.sha256}
                description={description}
                now={now}
                tone={tone}
                host={i === 0 ? host : undefined}
                source={i === 0 ? undefined : issuers[k]?.source}
                issuer={i > 0}
                actions={actions}
                detailActions={detailActions}
                guidance={guidance}
              />
            );
          })}
          {presented.chain && !presented.chain.complete && chain.length > 0 && (
            <StateRow
              icon="info"
              title="The root certificate wasn’t available, so this chain is incomplete."
              description={
                presented.chain.stop && STOP_REASONS[presented.chain.stop]
              }
            />
          )}
        </Rows>
      </Box>
    </div>
  );
}

function StateRow({
  icon,
  title,
  description,
  alert,
}: {
  icon: IconName;
  title: string;
  description?: string;
  alert?: boolean;
}) {
  return (
    <DetailRow role={alert ? "alert" : "status"}>
      <NeutralIconBadge iconName={icon} />
      <DetailLabel>
        <Text variant="text-regular-md">{title}</Text>
        {description && (
          <Text
            variant="text-small"
            color={alert ? "var(--red-600)" : "var(--text-secondary)"}
          >
            {description}
          </Text>
        )}
      </DetailLabel>
    </DetailRow>
  );
}

function RemoveButton({
  disabled,
  onRemove,
}: {
  disabled?: boolean;
  onRemove: () => void;
}) {
  return (
    <Button
      variant="subtle"
      icon="trash"
      disabled={disabled}
      onClick={onRemove}
      style={{ alignSelf: "flex-start" }}
    >
      Remove trusted certificate
    </Button>
  );
}
